/**
 * Einen Versandschritt überspringen – mit Begründung.
 *
 * Prüfung und Ankündigung sind vor dem Versand Pflicht. Manchmal gibt es
 * gute Gründe, einen davon auszulassen: Der Kunde hat selbst um sofortige
 * Zusendung gebeten, das Angebot ist eine Kleinigkeit ohne Lieferantenanteil.
 * Erlaubt ist das – aber nie stumm. Die Begründung bleibt am Vorgang und in
 * der Chronik stehen, mit Name und Tag.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer, Pflicht } from '../komponenten/Modal';
import type { Angebot, AngebotAntwort } from '../typen';

export type Versandschritt = 'pruefung' | 'ankuendigung';

const TEXTE: Record<Versandschritt, { titel: string; frage: string; beispiel: string; wirkung: string }> = {
  pruefung: {
    titel: 'Prüfung überspringen',
    frage: 'Warum geht dieses Angebot ungeprüft hinaus?',
    beispiel: 'z. B. Kleinauftrag ohne Lieferantenanteil, Preise aus der Stammliste',
    wirkung:
      'Die Prüfung gilt für dieses Angebot als erledigt. Die Begründung steht in der Chronik; sie lässt sich zurücknehmen, dann ist die Prüfung wieder fällig.',
  },
  ankuendigung: {
    titel: 'Ankündigung überspringen',
    frage: 'Warum wird der Kunde nicht vorab angerufen?',
    beispiel: 'z. B. Kunde hat per Mail um sofortige Zusendung gebeten',
    wirkung:
      'Der Ankündigungsanruf gilt für dieses Angebot als erledigt. Die Begründung steht in der Chronik; sie lässt sich zurücknehmen, dann ist der Anruf wieder fällig.',
  },
};

export function UeberspringenModal({
  angebot,
  schritt,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  schritt: Versandschritt;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();
  const [begruendung, setBegruendung] = useState('');
  const [fehler, setFehler] = useState('');
  const t = TEXTE[schritt];

  const mutation = useMutation({
    mutationFn: () =>
      api.sende<AngebotAntwort>(`/angebote/${angebot.id}/versand/ueberspringen`, {
        schritt,
        begruendung: begruendung.trim(),
      }),
    onSuccess: (antwort) => {
      onFertig();
      if (antwort.toast) toastZeigen(antwort.toast);
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  function speichern() {
    if (begruendung.trim().length < 3) {
      setFehler('Bitte eine Begründung eintragen – ein Satz genügt.');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      titel={t.titel}
      untertitel={`${angebot.name} · ${angebot.kunde.name}`}
      breite={480}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer onClick={speichern} disabled={mutation.isPending}>
            {mutation.isPending ? 'Wird gespeichert …' : 'Überspringen'}
          </KnopfPrimaer>
        </>
      }
    >
      <label className="modal-feld">
        <span>{t.frage}<Pflicht /></span>
        <textarea
          className={`modal-textarea ${fehler ? 'modal-eingabe--fehler' : ''}`}
          rows={3}
          placeholder={t.beispiel}
          value={begruendung}
          autoFocus
          onChange={(e) => {
            setBegruendung(e.target.value);
            setFehler('');
          }}
        />
      </label>

      <div className="modal-hinweis">{t.wirkung}</div>

      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
